import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "util/UserContext";
import api from "config/axiosConfig";
import Modal from "ui/Modal";
import { Drawer, DrawerContent } from "ui/Drawer";
import ScrollableTabsList from "ui/ScrollableTabsList";
import { Check, X, Loader2, UserIcon, Users } from "lucide-react";
import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import type { Actor } from "types/actor";
import { toast } from "sonner";
import { getEntityDetails, getActorDetails } from "util/getObjectDetails";
import { formatRelativeTime } from "config/dayjsConfig";
import { apiRoutes } from "util/apiRoutes";

/**
 * Build a canonical `Actor` from the loose actor-like fields carried
 * on a request row so the standardized `<ActorAvatar>` can render the
 * row's avatar with the same Instagram-style affordances as the rest
 * of the product.
 */
const buildRequestActor = (params: {
    id: number | undefined;
    name: string | undefined | null;
    type: 'user' | 'team' | undefined;
    properties_public:
        | { avatar_key?: string; avatar_url?: string; is_private?: boolean }
        | undefined;
}): Actor | null => {
    if (!params.id || params.id <= 0) {
        return null;
    }
    const username = (params.name || '').trim();
    if (!username) {
        return null;
    }
    const avatarKey = (params.properties_public?.avatar_key || '').trim();
    return {
        id: params.id,
        kind: params.type === 'team' ? 'team' : 'user',
        username,
        displayName: null,
        avatarKey: avatarKey.length > 0 ? avatarKey : null,
    };
};

interface RequestProps {
    id: number;
    requestor_id: number;
    requestor_type: "user" | "team";
    requestor_name?: string;
    requestor_display_name?: string;
    requestor_properties_public?: any;
    requestee_id: number;
    requestee_type: "user" | "team";
    requestee_name?: string;
    requestee_display_name?: string;
    requestee_properties_public?: any;
    entity_type?: string;
    entity_id?: number;
    entity_name?: string;
    entity_display_name?: string;
    entity_properties_public?: any;
    request_type: string;
    status: string;
    created_at: string;
    updated_at?: string;
}

interface RequestRespondModalProps {
    onClose: () => void;
    refRequestId?: number;
}

const RequestRespondModal: React.FC<RequestRespondModalProps> = ({ onClose, refRequestId }) => {
    const { currentUser, userIsMobile } = useUser();
    const [activeTab, setActiveTab] = useState("received");
    const [receivedRequests, setReceivedRequests] = useState<RequestProps[]>([]);
    const [sentRequests, setSentRequests] = useState<RequestProps[]>([]);
    const [loading, setLoading] = useState(true);
    const [processingRequest, setProcessingRequest] = useState<number | null>(null);

    // Fetch requests when component mounts or tab changes
    const fetchRequests = useCallback(async () => {
        if (!currentUser) return;
        
        setLoading(true);
        try {
            // Fetch received requests (where current user is requestee)
            const receivedResponse = await api.get(apiRoutes.requests.root, {
                params: {
                    requesteeId: currentUser.id,
                    requesteeType: "user",
                    status: "pending",
                    limit: 50,
                    offset: 0
                }
            });


            // Enrich received requests with names and properties
            const enrichedReceivedRequests = await Promise.all(
                (receivedResponse.data.requests || []).map(async (request: RequestProps) => {
                    try {
                        // Get requestor details
                        const requestorDetails = await getActorDetails(request.requestor_type, request.requestor_id);
                        
                        // Get entity details if available
                        let entityDetails = null;
                        if (request.entity_type && request.entity_id) {
                            if (request.entity_type === "team" || request.entity_type === "user") {
                                entityDetails = await getActorDetails(request.entity_type, request.entity_id);
                            } else {
                                entityDetails = await getEntityDetails(request.entity_type, request.entity_id);
                            }
                        }
                        const entityLabel =
                            request.entity_type === "team" || request.entity_type === "user"
                                ? entityDetails?.teamname || entityDetails?.username || "Unknown"
                                : entityDetails?.title || entityDetails?.entity_name || "Unknown";
                        return {
                            ...request,
                            requestor_name: requestorDetails?.entity_name || "Unknown",
                            requestor_display_name: requestorDetails?.entity_name || "Unknown",
                            requestor_properties_public: requestorDetails?.properties_public || {},
                            entity_name: entityLabel,
                            entity_display_name: entityLabel,
                            entity_properties_public: entityDetails?.properties_public || entityDetails?.properties || {}
                        };
                    } catch (error) {
                        console.error("Error enriching request:", error);
                        return request;
                    }
                })
            );

            // Fetch sent requests (where current user is requestor)
            const sentResponse = await api.get(apiRoutes.requests.root, {
                params: {
                    requestorId: currentUser.id,
                    requestorType: "user",
                    status: "pending",
                    limit: 50,
                    offset: 0
                }
            });


            // Enrich sent requests with names and properties
            const enrichedSentRequests = await Promise.all(
                (sentResponse.data.requests || []).map(async (request: RequestProps) => {
                    try {
                        // Get requestee details
                        const requesteeDetails = await getActorDetails(request.requestee_type, request.requestee_id);
                        
                        // Get entity details if available
                        let entityDetails = null;
                        if (request.entity_type && request.entity_id) {
                            if (request.entity_type === "team" || request.entity_type === "user") {
                                entityDetails = await getActorDetails(request.entity_type, request.entity_id);
                            } else {
                                entityDetails = await getEntityDetails(request.entity_type, request.entity_id);
                            }
                        }
                        const sentEntityLabel =
                            request.entity_type === "team" || request.entity_type === "user"
                                ? entityDetails?.teamname || entityDetails?.username || "Unknown"
                                : entityDetails?.title || entityDetails?.entity_name || "Unknown";
                        return {
                            ...request,
                            requestee_name: requesteeDetails?.entity_name || "Unknown",
                            requestee_display_name: requesteeDetails?.entity_name || "Unknown",
                            requestee_properties_public: requesteeDetails?.properties_public || {},
                            entity_name: sentEntityLabel,
                            entity_display_name: sentEntityLabel,
                            entity_properties_public: entityDetails?.properties_public || entityDetails?.properties || {}
                        };
                    } catch (error) {
                        console.error("Error enriching request:", error);
                        return request;
                    }
                })
            );

            // If refRequestId is provided, ensure it appears first in the appropriate list
            if (refRequestId) {
                const receivedHighlighted = enrichedReceivedRequests.find(req => req.id === refRequestId);
                const sentHighlighted = enrichedSentRequests.find(req => req.id === refRequestId);
                
                if (receivedHighlighted) {
                    const filteredRequests = enrichedReceivedRequests.filter(req => req.id !== refRequestId);
                    setReceivedRequests([receivedHighlighted, ...filteredRequests]);
                    setSentRequests(enrichedSentRequests);
                    setActiveTab("received");
                } else if (sentHighlighted) {
                    const filteredRequests = enrichedSentRequests.filter(req => req.id !== refRequestId);
                    setSentRequests([sentHighlighted, ...filteredRequests]);
                    setReceivedRequests(enrichedReceivedRequests);
                    setActiveTab("sent");
                } else {
                    setReceivedRequests(enrichedReceivedRequests);
                    setSentRequests(enrichedSentRequests);
                }
            } else {
                setReceivedRequests(enrichedReceivedRequests);
                setSentRequests(enrichedSentRequests);
            }
        } catch (error) {
            console.error("Error fetching requests:", error);
            toast.error("Failed to fetch requests");
        } finally {
            setLoading(false);
        }
    }, [currentUser, refRequestId]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests,]);

    // Handle request action (accept/reject/cancel)
    const handleRequestAction = async (requestId: number, action: "accepted" | "rejected" | "cancelled") => {
        if (!currentUser) return;
        
        setProcessingRequest(requestId);
        try {

            if (action === "cancelled") {
                await api.delete(apiRoutes.requests.cancel(requestId));
            } else {
                await api.patch(apiRoutes.requests.byId(requestId), {
                    status: action
                });
            }
            
            // Update the local state based on action
            if (activeTab === "received") {
                setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
            } else {
                setSentRequests(prev => prev.filter(req => req.id !== requestId));
            }
            
            toast.success(`Request ${action}`);
        } catch (error) {
            console.error(`Error ${action} request:`, error);
            toast.error(`Failed to ${action} request`);
        } finally {
            setProcessingRequest(null);
        }
    };

    const tabs = [
        { label: "Received", value: "received" },
        { label: "Sent", value: "sent" }
    ];

    // Get the appropriate requests based on active tab
    let requests = activeTab === "received" ? receivedRequests : sentRequests;

    const handleTabChange = (tab: string) => {
        if (tab === "sent") {
            setActiveTab("sent");
            requests = receivedRequests;
        } else if (tab === "received") {
            setActiveTab("received");
            requests = sentRequests;
        }
    }

    // Render the avatar for a request
    const renderAvatar = (request: RequestProps) => {
        const isReceived = activeTab === "received";
        const actorType = isReceived ? request.requestor_type : request.requestee_type;
        const actorName = isReceived ? request.requestor_name : request.requestee_name;
        const actorId = isReceived ? request.requestor_id : request.requestee_id;
        const properties_public = isReceived ? request.requestor_properties_public : request.requestee_properties_public;

        // For team-entity requests, the row's avatar represents the team
        // (Partea). Otherwise it represents the requestor / requestee.
        const subjectActor: Actor | null =
            request.entity_type === 'team'
                ? buildRequestActor({
                      id: request.entity_id,
                      name: request.entity_name,
                      type: 'team',
                      properties_public: request.entity_properties_public,
                  })
                : buildRequestActor({
                      id: actorId,
                      name: actorName,
                      type: actorType,
                      properties_public: properties_public,
                  });

        if (subjectActor) {
            return (
                <ActorAvatar
                    actor={subjectActor}
                    size={ACTOR_AVATAR_SIZES.inboxRow}
                />
            );
        }

        // Fallback when the row is missing the canonical fields.
        return (
            <span className="flex size-12 flex-shrink-0 items-center justify-center rounded-full bg-eoe-bg-hover text-sm font-semibold text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text">
                {actorType === "team" ? <Users className="size-5" /> : <UserIcon className="size-5" />}
            </span>
        );
    };

    // Generate request message with clickable links
    const getRequestMessage = (request: RequestProps) => {
        const isReceived = activeTab === "received";
        const actorName = isReceived ? request.requestor_name : request.requestee_name;
                
        switch (request.request_type) {
            case "follow_request":
                return isReceived ? (
                    <span>
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                        {" wants to follow you"}
                    </span>
                ) : (
                    <span>
                        {"You requested to follow "}
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                    </span>
                );
                    
            case "team_join_request":
                return isReceived ? (
                    <span>
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                        {" wants to join "}
                        <a href={`/partea/${request.entity_name}`} className="font-semibold ">
                            {request.entity_name}
                        </a>
                    </span>
                ) : (
                    <span>
                        {"You requested to join partea "}
                        <a href={`/partea/${request.entity_name}`} className="font-semibold ">
                            {request.entity_name}
                        </a>
                    </span>
                );
                    
            case "team_invite":
                return isReceived ? (
                    <span>
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                        {" invited you to join partea "}
                        <a href={`/partea/${request.entity_name}`} className="font-semibold ">
                            {request.entity_name}
                        </a>
                    </span>
                ) : (
                    <span>
                        {"You invited "}
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                        {" to join "}
                        <a href={`/partea/${request.entity_name}`} className="font-semibold ">
                            {request.entity_name}
                        </a>
                    </span>
                );
                    
            case "coauthor_invite":
                return isReceived ? (
                    <span>
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                        {" invited you to co-author an entity"}
                    </span>
                ) : (
                    <span>
                        {"You invited "}
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                        {" to co-author an entity"}
                    </span>
                );
                    
            default:
                return isReceived ? (
                    <span>
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                        {" sent you a request"}
                    </span>
                ) : (
                    <span>
                        {"You sent a request to "}
                        <a href={`/user/${actorName}`} className="font-semibold ">
                            {actorName}
                        </a>
                    </span>
                );
        }
    };

    const renderRequestTime = (request: RequestProps) => {
        return formatRelativeTime(request.created_at);
    };

    // Render action buttons for a request
    const renderActions = (request: RequestProps) => {
        const isProcessing = processingRequest === request.id;
        const isReceived = activeTab === "received";
        
        if (isProcessing) {
            return (
                <div className="flex justify-center items-center w-20">
                    <Loader2 className="size-5 animate-spin text-eoe-text-sub dark:text-eoe-dark-text-sub" />
                </div>
            );
        }
        
        if (isReceived) {
            return (
                <div className="flex gap-2">
                    <button
                        onClick={() => handleRequestAction(request.id, "accepted")}
                        className="p-2 rounded-full bg-eoe-icon dark:bg-eoe-dark-bg-hover text-green-600 dark:text-green-500 hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        aria-label="Accept"
                    >
                        <Check className="size-5" />
                    </button>
                    <button
                        onClick={() => handleRequestAction(request.id, "rejected")}
                        className="p-2 rounded-full bg-eoe-icon dark:bg-eoe-dark-bg-hover text-red-600 dark:text-red-500 hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        aria-label="Reject"
                    >
                        <X className="size-5" />
                    </button>
                </div>
            );
        } else {
            return (
                <button
                    onClick={() => handleRequestAction(request.id, "cancelled")}
                    className="p-2 rounded-full bg-eoe-icon dark:bg-eoe-dark-bg-hover text-red-600 dark:text-red-500 hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                    aria-label="Cancel"
                >
                    <X className="size-5" />
                </button>
            );
        }
    };

    const content = (
        <div className="flex flex-col h-full">
            <div className="px-4 py-2">
                <ScrollableTabsList
                    tabs={tabs}
                    selectedTab={activeTab}
                    pageTitle="Requests"
                    onTabChange={handleTabChange}
                    navlink={false}
                />
            </div>
            
            <div className="flex-1 overflow-y-auto px-4 py-2">
                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <Loader2 className="size-8 animate-spin text-eoe-text-sub dark:text-eoe-dark-text-sub" />
                    </div>
                ) : requests.length === 0 ? (
                    <div className="flex flex-col justify-center items-center h-40 text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        <p className="text-center">
                            {activeTab === "received" 
                                ? "No pending requests received" 
                                : "No pending requests sent"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {requests.map((request) => (
                            <div 
                                key={request.id} 
                                className={`flex items-center gap-3 p-3 rounded-lg ${
                                    refRequestId === request.id 
                                        ? "bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal-hover" 
                                        : "bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover"
                                }`}
                            >
                                {renderAvatar(request)}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
                                        {getRequestMessage(request)}
                                    </div>
                                    <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {renderRequestTime(request)}
                                    </p>
                                </div>
                                {renderActions(request)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    return userIsMobile ? (
        <Drawer open onOpenChange={(open) => !open && onClose()}>
            <DrawerContent className="rounded-t-xl" title="Requests">
                {content}
            </DrawerContent>
        </Drawer>
    ) : (
        <Modal
            isOpen={true}
            onClose={onClose}
            title="Requests"
        >
            {content}
        </Modal>
    );
};

export default RequestRespondModal;
